import { apiClient } from "./client";
import type { PurchaseOrderSummary } from "../types/purchaseOrder";
import type {
  EquipmentInstanceCatalogEntry,
  RequisitionItemCatalogEntry,
  RequisitionListItem,
} from "../types/requisition";
import type { OfflineComponentTreeNode } from "../types/offline";
import type { VendorListItem } from "../types/vendor";
import type { VesselId } from "../types/vessel";

export interface OfflineBootstrapResponse {
  data: {
    server_time: string;
    vessel_id: VesselId;
    requisitions: RequisitionListItem[];
    purchase_orders: PurchaseOrderSummary[];
    vendors: VendorListItem[];
    item_catalog: RequisitionItemCatalogEntry[];
    equipment_instances: EquipmentInstanceCatalogEntry[];
    component_tree: OfflineComponentTreeNode[];
  };
}

interface OfflineDeltaResponse<TData> {
  data: TData[];
  server_time: string;
}

interface OfflineDeltaParams {
  vessel_id?: VesselId;
  updated_since?: string | null;
  page_size?: number;
}

export async function getOfflineBootstrap(params: OfflineDeltaParams = {}): Promise<OfflineBootstrapResponse> {
  const response = await apiClient.get<OfflineBootstrapResponse>("/purchase/offline/bootstrap/", { params });
  return response.data;
}

export async function listOfflineRequisitions(
  params: OfflineDeltaParams = {},
): Promise<OfflineDeltaResponse<RequisitionListItem>> {
  const response = await apiClient.get<OfflineDeltaResponse<RequisitionListItem>>("/purchase/offline/requisitions/", {
    params,
  });
  return response.data;
}

export async function listOfflinePurchaseOrders(
  params: OfflineDeltaParams = {},
): Promise<OfflineDeltaResponse<PurchaseOrderSummary>> {
  const response = await apiClient.get<OfflineDeltaResponse<PurchaseOrderSummary>>(
    "/purchase/offline/purchase-orders/",
    { params },
  );
  return response.data;
}

export async function listOfflineVendors(params: OfflineDeltaParams = {}): Promise<OfflineDeltaResponse<VendorListItem>> {
  const response = await apiClient.get<OfflineDeltaResponse<VendorListItem>>("/purchase/offline/vendors/", {
    params,
  });
  return response.data;
}

export async function listOfflineItemCatalog(
  params: OfflineDeltaParams = {},
): Promise<OfflineDeltaResponse<RequisitionItemCatalogEntry>> {
  const response = await apiClient.get<OfflineDeltaResponse<RequisitionItemCatalogEntry>>(
    "/purchase/offline/items/",
    { params },
  );
  return response.data;
}

export async function listOfflineEquipmentInstances(
  params: OfflineDeltaParams = {},
): Promise<OfflineDeltaResponse<EquipmentInstanceCatalogEntry>> {
  const response = await apiClient.get<OfflineDeltaResponse<EquipmentInstanceCatalogEntry>>(
    "/purchase/offline/equipment-instances/",
    { params },
  );
  return response.data;
}

export async function getOfflineComponentTree(
  params: OfflineDeltaParams = {},
): Promise<OfflineDeltaResponse<OfflineComponentTreeNode>> {
  const response = await apiClient.get<OfflineDeltaResponse<OfflineComponentTreeNode>>(
    "/purchase/offline/component-tree/",
    { params },
  );
  return response.data;
}
